import {Component, computed, effect, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormControl, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {PromptsService} from '../../../services/prompts.service';
import {CreatePrompt, Prompt, PromptModel, Visibility} from '../../../models/prompt.model';
import {UpdateEntity} from '../../../models/entity.model';
import {CreatePromptVersion} from '../../../models/prompt-version.model';
import {SpinnerComponent} from '../../spinner/spinner.component';
import {EditableComponent, EditableSaveResult} from '../../shared/editable/editable.component';
import {ToastService} from '../../../services/toast.service';
import {EditableTextComponent, EditableTextSaveResult} from '../../shared/editable-text/editable-text.component';
import {PageComponent, PageFooterDirective} from '../../shared/page/page.component';
import {FormAndControls} from '../../shared/utils';
import {MarkdownComponent} from '../../shared/markdown/markdown.component';
import {ButtonComponent} from '../../shared/button/button.component';
import {VisibilityComponent} from '../../shared/visibility/visibility.component';
import {VisibilitySelectorComponent} from '../../shared/visibility-selector/visibility-selector.component';
import {UpdatePack} from '../../../models/pack.model';
import {OrganizationService} from '../../../services/organization.service';
import {PageTabDirective} from '../../shared/page/page-tab.directive';
import {NavigateService} from '../../../services/navigate.service';
import {RoleService} from '../../../services/role.service';

interface PromptState {
  create?: boolean;
  loading?: boolean;
  error?: string;
}

interface PromptFormDef {
  name: string;
  description: string;
  visibility: Visibility;
  initial_content: string;
  comment: string;
}

@Component({
  selector: 'app-prompt-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, SpinnerComponent, RouterLink, EditableComponent, EditableTextComponent, PageComponent, PageFooterDirective, MarkdownComponent, ButtonComponent, VisibilityComponent, VisibilitySelectorComponent, PageTabDirective],
  templateUrl: './prompt-detail.component.html',
  styleUrl: './prompt-detail.component.scss',
  host: {
    class: 'app-container'
  }
})
export class PromptDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute)
  private readonly navigate = inject(NavigateService)
  private readonly promptsService = inject(PromptsService)
  private readonly roleService = inject(RoleService)
  protected readonly toastService = inject(ToastService)
  protected readonly orgService = inject(OrganizationService)

  protected readonly prompt = signal<PromptModel | null>(null);
  protected readonly error = signal<string | null>(null);
  protected readonly state = signal<PromptState>({});

  // Computed property for edit mode based on permissions
  protected readonly editMode = computed(() => {
    const canEdit = this.roleService.canEdit();
    const creating = this.state().create;

    if (creating && !canEdit) {
      return 'never'; // No permission to create
    }

    if (creating) {
      return 'always'; // Creating a new prompt
    }

    // Editing existing prompt - check permission
    return canEdit ? 'manual' : 'never';
  });

  protected form = this.createForm({
    name: '',
    description: '',
    visibility: 'private',
    initial_content: '',
    comment: '',
  })

  private createForm(def: PromptFormDef) {
    return new FormAndControls<PromptFormDef>({
      name: new FormControl(def.name, {validators: [Validators.required], nonNullable: true}),
      description: new FormControl(def.description, {nonNullable: true}),
      visibility: new FormControl(def.visibility, {nonNullable: true}),
      comment: new FormControl(def.comment),
      initial_content: new FormControl(def.initial_content, {validators: [Validators.required], nonNullable: true}),
    })
  }

  constructor() {
    effect(() => {
      const result = this.prompt()
      if (result) {
        this.form = this.createForm({
          name: result.name,
          description: result.description || '',
          visibility: result.visibility,
          comment: '',
          initial_content: result.latestVersion?.content || '',
        })
      }
    })
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const name = params.get('name');
      if (name && name !== 'new') {
        this.loadPrompt(name);
      } else if (this.roleService.assertCanEdit()) {
        this.updateState({create: true})
      } else {
        this.navigate.prompts()
      }
    })
  }

  protected updateState(state: Partial<PromptState>): void {
    this.state.update(prevState => ({...prevState, ...state}));
  }

  private loadPrompt(name: string): void {
    this.updateState({loading: true, error: undefined});

    this.toastService.handleResult(
      'Load Prompt',
      this.promptsService.get(name),
      {showSuccess: false}
    ).subscribe({
      next: (result) => {
        this.prompt.set(result)
      },
      error: (_err) => {
        this.goBack()
      },
      complete: () => {
        this.updateState({loading: false});
      }
    });
  }

  savePrompt(field: keyof UpdatePack, result: EditableTextSaveResult): void
  savePrompt(field: keyof UpdatePack, result: EditableSaveResult, control: FormControl): void
  savePrompt(
    field: keyof UpdatePack,
    result: EditableTextSaveResult | EditableSaveResult,
    control?: FormControl
  ): void {
    const prompt = this.prompt();

    if (!prompt) return;

    const request: UpdateEntity<Prompt> = {id: prompt.id};
    (request as any)[field] = ('formControl' in result) ? result.formControl.value : control!.value;

    const op = this.promptsService.update(request)
    result.success = op

    this.toastService.handleResult('Update Prompt', op, (updatedPrompt) => {
      this.prompt.set(updatedPrompt);
    });
  }

  createPrompt() {
    if (this.form.invalid) return

    const request: CreatePrompt = this.form.value as CreatePrompt;
    this.toastService.handleResult(
      'Create Prompt',
      this.promptsService.create(request),
      (prompt) => {
        this.navigate.prompt(prompt);
      }
    )
  }

  savePromptContent(result: EditableSaveResult): void {
    const [content, comment] = [this.form.controls.initial_content, this.form.controls.comment]

    if (content.invalid || comment.invalid) {
      result.success = false
    } else {
      const prompt = this.prompt();

      if (!prompt) {
        result.success = false;
        return;
      }

      const request: CreatePromptVersion = {content: content.value!, comment: comment.value!}
      result.success = this.promptsService.createVersion(prompt, request)
      this.toastService.handleResult('Update Prompt', result.success, (prompt) => {
        this.prompt.set(prompt)
      })
    }
  }

  goBack(): void {
    this.navigate.prompts()
  }
}
